/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "dbt Cloud - Trigger syncs and sync models", description = "The dbt Cloud extension lets you both schedule Hightouch syncs to run as soon as a dbt Cloud job completes, and to sync models from dbt into Hightouch. Relying on dbt Cloud for sync scheduling ensures that Hightouch performs syncs when fresh data is available.", lastUpdatedDate = new Date(1775001600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "dbt Cloud - Trigger syncs and sync models",
        description: "The dbt Cloud extension lets you both schedule Hightouch syncs to run as soon as a dbt Cloud job completes, and to sync models from dbt into Hightouch. Relying on dbt Cloud for sync scheduling ensures that Hightouch performs syncs when fresh data is available.",
        lastUpdatedDate: "Wed Apr 01 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Connect to dbt Cloud",
          "depth": 0,
          "id": "connect-to-dbt-cloud"
        }, {
          "value": "Configure sync schedules",
          "depth": 0,
          "id": "configure-sync-schedules"
        }, {
          "value": "dbt Cloud model sync",
          "depth": 0,
          "id": "dbt-cloud-model-sync"
        }, {
          "value": "Connecting a source",
          "depth": 1,
          "id": "connecting-a-source"
        }, {
          "value": "Syncing models",
          "depth": 1,
          "id": "syncing-models"
        }, {
          "value": "dbt Cloud model sync advantages",
          "depth": 1,
          "id": "dbt-cloud-model-sync-advantages"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      ul: "ul",
      li: "li",
      h2: "h2",
      a: "a",
      strong: "strong",
      code: "code",
      br: "br",
      ol: "ol",
      img: "img",
      h3: "h3"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(Info, {
        children: _jsx(_components.p, {
          children: "This extension requires a paid Hightouch plan"
        })
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "dbt requires you to have a paid plan to use this extension"
        })
      }), "\n", _jsx(_components.p, {
        children: "The dbt Cloud extension allows you to:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Schedule Hightouch syncs to run as soon as a dbt Cloud job completes. Relying on dbt Cloud for sync scheduling ensures that Hightouch performs syncs when fresh data is available."
        }), "\n", _jsx(_components.li, {
          children: "Sync models from dbt into Hightouch"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Extension setup requires connecting your dbt account to Hightouch."
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-dbt-cloud",
        children: "Connect to dbt Cloud"
      }), "\n", _jsxs(_components.p, {
        children: ["Open the ", _jsx(_components.a, {
          href: "https://app.hightouch.com/extensions/dbt-cloud/configuration?from=docs",
          children: "dbt Cloud configuration page"
        }), " in your workspace and enter the ", _jsx(_components.strong, {
          children: "dbt Cloud URL"
        }), ". Depending on your setup, you need to insert:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["for regular dbt Cloud setups, a ", _jsx(_components.a, {
            href: "https://docs.getdbt.com/docs/cloud/about-cloud/access-regions-ip-addresses",
            children: "default access URL"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["for accounts with cell-based account prefixes, you can find the custom ", _jsx(_components.a, {
            href: "https://docs.getdbt.com/docs/cloud/about-cloud/access-regions-ip-addresses#api-access-urls",
            children: "unique access URL"
          }), " in dbt on the ", _jsx(_components.strong, {
            children: "Account settings"
          }), " page (below the ", _jsx(_components.strong, {
            children: "Account information"
          }), " pane)"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "These are the most commonly used default access URLs:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "​­­https://cloud.getdbt.com​"
          }), ", for the US"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "­https://emea.getdbt.com​"
          }), ", for EMEA"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Then navigate to dbt Cloud and generate a ", _jsx(_components.a, {
          href: "https://docs.getdbt.com/docs/dbt-cloud-apis/service-tokens",
          children: "service account token"
        }), " or a ", _jsx(_components.a, {
          href: "https://docs.getdbt.com/docs/dbt-cloud-apis/user-tokens",
          children: "personal access token (PAT)"
        }), ". In Hightouch, enter your token in the ", _jsx(_components.strong, {
          children: "dbt Cloud API token"
        }), " field in the ", _jsx(_components.strong, {
          children: "Configuration"
        }), " tab of the dbt Cloud extension. Click ", _jsx(_components.strong, {
          children: "Connect"
        }), " to complete your connection configuration."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["A ", _jsx(_components.strong, {
            children: "Member-level or higher"
          }), " ", _jsx(_components.a, {
            href: "https://docs.getdbt.com/docs/dbt-cloud-apis/service-tokens#team-plans-using-service-account-tokens",
            children: "service account token"
          }), " is required.", _jsx(_components.br, {}), "\n", "Read-only tokens are no longer supported because the dbt Cloud integration uses webhooks, which require elevated permissions."]
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["If you previously used a read-only dbt Cloud token, you must update to a ", _jsx(_components.strong, {
            children: "Member"
          }), " token (or higher). Without this change, Hightouch syncs triggered by dbt Cloud will fail."]
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["To update your dbt Cloud API token in Hightouch, edit your credentials within the extension and set a new API token. This won't reset the schedule configuration of syncs that use the ", _jsx(_components.a, {
            href: "#configure-your-sync-schedule",
            children: "dbt Cloud scheduling"
          }), " method, nor are any synced models removed."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "configure-sync-schedules",
        children: "Configure sync schedules"
      }), "\n", _jsxs(_components.p, {
        children: ["When creating or modifying a sync you can select ", _jsx(_components.strong, {
          children: "dbt Cloud"
        }), " as a schedule type."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go the ", _jsx(_components.strong, {
            children: "Schedule"
          }), " tab of overview page of the sync to schedule. Select ", _jsx(_components.strong, {
            children: "dbt Cloud"
          }), " as the schedule type."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the appropriate dbt ", _jsx(_components.strong, {
            children: "Account"
          }), " and ", _jsx(_components.strong, {
            children: "Job"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Save"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/dbt-configure.png",
          alt: "dbt Cloud schedule configuration in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "Your sync is now scheduled to run whenever your chosen dbt Cloud jobs complete."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "The sync won't get triggered if your dbt Cloud job fails."
        })
      }), "\n", _jsx(_components.h2, {
        id: "dbt-cloud-model-sync",
        children: "dbt Cloud model sync"
      }), "\n", _jsxs(_components.p, {
        children: ["To sync models from dbt Cloud to Hightouch, the extension requires a ", _jsx(_components.strong, {
          children: "Member-level or higher"
        }), " service account token as described\nin the preceding section. A webhook ID should display next to your account status."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/dbt-connections.png",
          alt: "dbt Cloud connections"
        })
      }), "\n", _jsx(_components.h3, {
        id: "connecting-a-source",
        children: "Connecting a source"
      }), "\n", _jsxs(_components.p, {
        children: ["On the ", _jsx(_components.a, {
          href: "https://app.hightouch.com/extensions/dbt-cloud/configuration?from=docs",
          children: "extension configuration page"
        }), " there is a list of sources from your workspace within Hightouch that can be\nconnected to dbt Cloud. Choose one that you would like to configure and click \"Connect to dbt.\""]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/dbt-connect-source.png",
          alt: "dbt Cloud - connection configuration"
        })
      }), "\n", _jsx(_components.p, {
        children: "Configure the dbt Cloud account and job from the dropdowns, then choose \"Create Configuration\""
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/dbt-connect-source-config.png",
          alt: "dbt Cloud - connection configuration details"
        })
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Select a dbt job that has a \"Compile\" step (", _jsx(_components.code, {
            children: "compile"
          }), ", ", _jsx(_components.code, {
            children: "build"
          }), ", ", _jsx(_components.code, {
            children: "run"
          }), ") or no model artifacts get produced."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "syncing-models",
        children: "Syncing models"
      }), "\n", _jsx(_components.p, {
        children: "Once configured, the \"dbt to Hightouch\" status shows \"Waiting to be synced.\"  If your job in dbt Cloud has previous\nruns you can click the \"refresh\" button to manually sync models from the job's result. Otherwise, run the job within dbt\nCloud and the result should trigger the update in Hightouch."
      }), "\n", _jsx(_components.p, {
        children: "After a successful run, dbt Cloud models are available within Hightouch."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/dbt-cloud-model-picker.png",
          alt: "dbt cloud model picker"
        })
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "A dbt Cloud job can fail and still produce model artifacts which sync to Hightouch."
        })
      }), "\n", _jsx(_components.h3, {
        id: "dbt-cloud-model-sync-advantages",
        children: "dbt Cloud model sync advantages"
      }), "\n", _jsxs(_components.p, {
        children: ["Along with the ", _jsx(_components.a, {
          href: "/extensions/dbt-models#dbt-model-advantages",
          children: "advantages of using dbt models"
        }), " that dbt Core brings,\ndbt Cloud brings more helpful features:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["dbt Mesh: reference models in other projects (", _jsx(_components.code, {
            children: "{{ ref('customers', 'contact_info') }}"
          }), ")"]
        }), "\n", _jsx(_components.li, {
          children: "\"Versionless\" (now called \"Latest\") - always runs your compile job with the latest dbt version"
        }), "\n", _jsx(_components.li, {
          children: "Private dependencies"
        }), "\n", _jsx(_components.li, {
          children: "Database adapters that Hightouch doesn't support for dbt Core (such as Fabric)"
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
